import React from 'react';

import { Button, Form, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { Operator, TextType } from 'common/filters/Filters.types';
import { Select } from 'core/select/Select';

export interface RuleType {
    field: string;
    operator: number;
    value: string;
}

interface RuleProps {
    rule: RuleType;
    groupId: string;
    // ruleIndex is no longer used for keys—just to know which to update
    ruleIndex: number;
    onRuleChange: (groupId: string, ruleIndex: number, key: keyof Omit<RuleType, 'id'>, value: string) => void;
    onRemove: (groupId: string, ruleIndex: number) => void;
    showRemoveButton: boolean;
}
interface OperatorItem {
    value: number;
    label: string;
}

/**
 * Rule Component
 * Renders a single filter rule row using React-Bootstrap
 */
export const Rule: React.FC<RuleProps> = ({ rule, groupId, ruleIndex, onRuleChange, onRemove, showRemoveButton }) => {
    const { t } = useTranslation(['common/filters']);
    // Validate keywords: max 20, alphanumeric and spaces only
    const keywords = rule.value
        .split(',')
        .map((kw) => kw.trim())
        .filter((kw) => kw !== '');

    let error: string | null = null;

    if (keywords.length > 20) {
        error = t('CANNOT_BE_MORE_THAN_20');
    } else {
        // eslint-disable-next-line no-lonely-if
        if (keywords.some((kw) => !/^[a-zA-Z0-9 #&@*!+-_]+$/.test(kw))) {
            error = t('VALUE_TO_BE_ALPHANUM');
        }
    }

    // Define operators as objects with code and label
    const RULE_OPERATORS: OperatorItem[] = [
        { value: Operator.ContainsWord, label: t('CONTAINS') },
        { value: Operator.NotContains, label: t('DOES_NOT_CONTAIN') },
    ];

    const RULE_MENTION_TYPES = [{ label: t('MENTIONS_TEXT'), value: TextType.MentionText }];

    return (
        <>
            <Form.Row className="align-items-center mb-2">
                <Col md={6}>
                    <Select
                        options={RULE_MENTION_TYPES}
                        value={RULE_MENTION_TYPES.filter((option) => option.value === rule.field)}
                        onChange={(selectedOption: any) =>
                            onRuleChange(groupId, ruleIndex, 'field', selectedOption.value)
                        }
                    />
                </Col>
                <Col md={6}>
                    <Select
                        options={RULE_OPERATORS}
                        value={RULE_OPERATORS.filter((option) => option.value === rule.operator)}
                        onChange={(selectedOption: any) =>
                            onRuleChange(groupId, ruleIndex, 'operator', selectedOption.value)
                        }
                    />
                </Col>
            </Form.Row>
            <Form.Row className="align-items-center mb-3">
                <Col md={showRemoveButton ? 11 : 12}>
                    <Form.Control
                        type="text"
                        placeholder={t('RULE_VALUE_PLACEHOLDER')}
                        value={rule.value}
                        onChange={(e) => onRuleChange(groupId, ruleIndex, 'value', e.target.value)}
                        isInvalid={!!error}
                    />
                    {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
                </Col>
                {showRemoveButton && (
                    <Col md={1} className="text-right">
                        <Button variant="outline-danger" size="sm" onClick={() => onRemove(groupId, ruleIndex)}>
                            &times;
                        </Button>
                    </Col>
                )}
            </Form.Row>
        </>
    );
};
